import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const SUCCESS_PARAM = 'notify_me_success';

// Hoist notify-me dialogs from <template> into <body> so the customer form
// is not nested inside the outer product form (which would cause the inner
// form tag to be dropped by the HTML parser and submissions to redirect to
// /cart/add — "product niet op voorraad").
function hoistDialogs() {
  document.querySelectorAll('template[data-notify-me-template]').forEach((template) => {
    const id = template.dataset.notifyMeTemplate;
    if (!id || document.getElementById(id)) {
      template.remove();
      return;
    }
    document.body.appendChild(template.content.cloneNode(true));
    template.remove();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hoistDialogs, { once: true });
} else {
  hoistDialogs();
}

class NotifyMeFormComponent extends Component {
  #abortController = new AbortController();
  #currentVariant = null;
  #successCloseTimer = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    const variantNameEl = this.querySelector('[data-notify-me-variant-name]');
    this.#currentVariant = {
      id: variantNameEl?.dataset.defaultVariantId || '',
      title: variantNameEl?.dataset.defaultText || '',
      sku: variantNameEl?.dataset.defaultSku || '',
    };

    const dialog = this.closest('dialog');
    if (!dialog) return;

    // Trigger buttons can be anywhere on the page (still inside their original
    // product form — fine, ze submitten niet omdat type="button").
    const triggers = document.querySelectorAll(`[data-notify-me-target="${dialog.id}"]`);
    triggers.forEach((btn) => {
      btn.addEventListener('click', () => this.#openDialog(), { signal });
    });

    const closeBtn = this.querySelector('[data-notify-me-close]');
    closeBtn?.addEventListener('click', () => dialog.close(), { signal });

    // Click-outside-to-close
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    }, { signal });

    // Variant updates bubble to document via ThemeEvents.variantUpdate.
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#onSubmit, { signal });

    // Detect success-redirect from return_to.
    const params = new URLSearchParams(window.location.search);
    if (params.get(SUCCESS_PARAM) === 'true') {
      this.#openDialog();
      this.#showSuccess();
      const url = new URL(window.location.href);
      url.searchParams.delete(SUCCESS_PARAM);
      window.history.replaceState({}, '', url.toString());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    const productId = event.detail?.data?.productId || event.detail?.product?.id;
    if (!variant) return;
    if (productId && String(productId) !== String(this.dataset.productId)) return;

    this.#currentVariant = {
      id: variant.id,
      title: variant.title || this.#currentVariant.title,
      sku: variant.sku || '',
    };

    const variantNameEl = this.querySelector('[data-notify-me-variant-name]');
    if (variantNameEl) variantNameEl.textContent = this.#currentVariant.title;

    this.#updateNote();
  };

  #updateNote() {
    const noteInput = this.querySelector('[data-notify-me-note]');
    if (!noteInput) return;
    const lines = [
      'Notify-me ingediend voor:',
      `Product: ${this.dataset.productTitle || ''}`,
      `Variant: ${this.#currentVariant.title}`,
      `Variant ID: ${this.#currentVariant.id}`,
      `SKU: ${this.#currentVariant.sku || '(geen)'}`,
    ];
    noteInput.value = lines.join('\n');

    // Make the redirect-URL variant-aware. Without this, Shopify redirect lands
    // op product.url zonder ?variant=, waardoor de pagina opnieuw rendert met de
    // eerste variant en het success-modal de verkeerde maat toont.
    const returnInput = this.querySelector('input[name="return_to"]');
    if (returnInput && this.#currentVariant?.id) {
      const baseUrl = (returnInput.value || '').split('?')[0];
      returnInput.value = `${baseUrl}?notify_me_success=true&variant=${encodeURIComponent(this.#currentVariant.id)}`;
    }
  }

  #openDialog() {
    const dialog = this.closest('dialog');
    if (!dialog || dialog.open) return;
    this.#resetForm();
    this.#syncVariantFromPicker();
    this.#updateNote();
    dialog.showModal();
  }

  // Read the currently-selected variant straight from the variant-picker's
  // checked radios. Synchronous, so it side-steps the race waar
  // VariantUpdateEvent pas NA de section-fetch wordt ge-dispatcht.
  #syncVariantFromPicker() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!picker) return;

    const fieldsets = picker.querySelectorAll('fieldset.variant-option');
    const values = [];
    let lastVariantId = '';

    fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      if (!checked) return;
      if (checked.value) values.push(checked.value);
      if (checked.dataset.variantId) lastVariantId = checked.dataset.variantId;
    });

    // Fall back to <select> dropdowns if the theme renders the picker as dropdowns.
    if (!lastVariantId) {
      const selects = picker.querySelectorAll('select');
      selects.forEach((select) => {
        const opt = select.options[select.selectedIndex];
        if (!opt) return;
        if (opt.value) values.push(opt.value);
        if (opt.dataset.variantId) lastVariantId = opt.dataset.variantId;
      });
    }

    if (!lastVariantId) return;

    if (String(lastVariantId) !== String(this.#currentVariant?.id)) {
      this.#currentVariant = {
        id: lastVariantId,
        title: values.join(' / ') || this.#currentVariant?.title || '',
        sku: '',
      };
      const variantNameEl = this.querySelector('[data-notify-me-variant-name]');
      if (variantNameEl) variantNameEl.textContent = this.#currentVariant.title;
    }
  }

  #resetForm() {
    if (this.#successCloseTimer) {
      clearTimeout(this.#successCloseTimer);
      this.#successCloseTimer = null;
    }

    this.#hideMessages();

    this.querySelectorAll('.notify-me-form__fields, .notify-me-form__submit').forEach((el) => {
      el.style.display = '';
    });

    const submitButton = this.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = false;
      if (submitButton.dataset.originalText) {
        submitButton.textContent = submitButton.dataset.originalText;
        delete submitButton.dataset.originalText;
      }
    }
  }

  #onSubmit = (event) => {
    const form = event.currentTarget;
    const emailInput = form.querySelector('input[type="email"]');
    if (!emailInput) return;

    const email = emailInput.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      event.preventDefault();
      this.#showError('Voer een geldig e-mailadres in.');
      return;
    }

    this.#hideMessages();
    this.#syncVariantFromPicker();
    this.#updateNote();

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.dataset.originalText = submitButton.textContent;
      submitButton.textContent = 'Bezig...';
    }
    // Form submits naturally — Shopify redirects naar return_to met
    // ?notify_me_success=true en de pagina wordt herladen.
  };

  #showSuccess() {
    const successEl = this.querySelector('[data-notify-me-success]');
    successEl?.classList.remove('hidden');

    this.querySelectorAll('.notify-me-form__fields, .notify-me-form__submit').forEach((el) => {
      el.style.display = 'none';
    });

    if (this.#successCloseTimer) clearTimeout(this.#successCloseTimer);
    this.#successCloseTimer = setTimeout(() => {
      this.closest('dialog')?.close();
      this.#successCloseTimer = null;
    }, 5000);
  }

  #showError(message) {
    const errorEl = this.querySelector('[data-notify-me-error]');
    const errorText = this.querySelector('[data-notify-me-error-text]');
    if (errorText) errorText.textContent = message;
    errorEl?.classList.remove('hidden');
  }

  #hideMessages() {
    this.querySelector('[data-notify-me-error]')?.classList.add('hidden');
    this.querySelector('[data-notify-me-success]')?.classList.add('hidden');
  }
}

if (!customElements.get('notify-me-form-component')) {
  customElements.define('notify-me-form-component', NotifyMeFormComponent);
}
